import { useMeta } from 'vue-meta'
import { useRoute } from 'vue-router'
import { useGlobalStore } from '@/stores/global'
import { computed } from 'vue'

/**
 * Composable for managing meta information across all routes
 * @param {Object} options - Options for meta management
 * @param {Object} options.defaultTitle - Default title to use when no specific title is provided
 * @param {Object} options.defaultDescription - Default description to use when no specific description is provided
 * @param {Object} options.titleSuffix - Suffix to append to all titles
 * @returns {Object} - Meta management functions
 */
export function useMetaManager(options = {}) {
  const route = useRoute()
  const globalStore = useGlobalStore()

  // Default options
  const defaultTitle = options.defaultTitle || 'Future Proof Scan'
  const defaultDescription = options.defaultDescription || ''
  const titleSuffix = options.titleSuffix || ' | Future Proof Scan'

  // Initialize meta with empty object
  const { meta } = useMeta({})

  /**
   * Update meta information based on provided data
   * @param {Object} data - Data containing meta information
   * @param {string} data.title - Page title
   * @param {string} data.metaDescription - Page description
   */
  const updateMeta = (data) => {
    if (!data) return

    // Update title
    if (data.title) {
      meta.title = `${data.title}${titleSuffix}`
    } else if (route.params.slug) {
      // Generate title from slug if no title is provided
      const slugTitle = route.params.slug
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      meta.title = `${slugTitle}${titleSuffix}`
    } else {
      meta.title = defaultTitle
    }

    // Update description
    if (data.metaDescription) {
      meta.description = data.metaDescription
    } else {
      meta.description = defaultDescription
    }

    // Update canonical URL
    meta.link = [
      {
        rel: 'canonical',
        href: globalStore.getProductionWebsiteUrl + route.path
      }
    ]
  }

  /**
   * Reset meta information to defaults
   */
  const resetMeta = () => {
    meta.title = defaultTitle
    meta.description = defaultDescription
    meta.link = [
      {
        rel: 'canonical',
        href: globalStore.getProductionWebsiteUrl + route.path
      }
    ]
  }

  return {
    updateMeta,
    resetMeta,
    meta
  }
}
